// Sticky section stepper — one dot per section, connectors turning done-green
// as prior sections complete, and a meta row naming the active section with its
// n/n count. Dots jump-scroll; the overlay drives `activeId` from scroll.

import React from 'react';
import styles from './ConversationalForms.module.css';
import { CfField, CfSection } from './types';

export type StepperSection = CfSection & { fields: CfField[] };

type SectionStepperProps = {
    sections: StepperSection[];
    activeId: string;
    isConfirmed: (fieldId: string) => boolean;
    onJump: (sectionId: string) => void;
};

/** The fields a section's count and done-state derive from — its required
 *  fields, or all of them when it has none (the all-optional fallback that
 *  mirrors `progressPct`, store tracker 6.25: "0/0 done" claimed completeness
 *  on a section that had not been touched). */
const countedOf = (section: StepperSection) => {
    const required = section.fields.filter((f) => !f.optional);
    return required.length ? required : section.fields;
};

const SectionStepper = ({ sections, activeId, isConfirmed, onJump }: SectionStepperProps) => {
    const active = sections.find((s) => s.id === activeId) ?? sections[0];
    if (!active) return null;
    const activeCounted = countedOf(active);
    const activeDone = activeCounted.filter((f) => isConfirmed(f.id)).length;

    const doneOf = (section: StepperSection) => {
        const counted = countedOf(section);
        return {
            done: counted.filter((f) => isConfirmed(f.id)).length,
            total: counted.length,
        };
    };

    return (
        <div className={styles.stepper} data-testid="cf-stepper">
            <div className={styles.stepperDots}>
                {sections.map((section, i) => {
                    const { done, total } = doneOf(section);
                    const complete = total > 0 && done === total;
                    const partial = done > 0 && !complete;
                    const priorDone = sections.slice(0, i).every((s) => {
                        const p = doneOf(s);
                        return p.total > 0 && p.done === p.total;
                    });
                    const isActive = section.id === active.id;
                    const stateClass = complete
                        ? styles.stepDone
                        : partial
                            ? styles.stepPartial
                            : '';
                    return (
                        <React.Fragment key={section.id}>
                            {i > 0 && (
                                <span
                                    className={`${styles.stepLine} ${priorDone ? styles.stepLineDone : ''}`}
                                    aria-hidden="true"
                                />
                            )}
                            <button
                                type="button"
                                className={`${styles.stepDot} ${isActive ? styles.stepActive : ''} ${stateClass}`}
                                onClick={() => onJump(section.id)}
                                aria-label={`Section ${i + 1} of ${sections.length}: ${section.title}${complete ? ' — complete' : partial ? ' — in progress' : ''}`}
                                aria-current={isActive ? 'step' : undefined}
                            >
                                {complete ? '✓' : i + 1}
                            </button>
                        </React.Fragment>
                    );
                })}
            </div>
            <div className={styles.stepperMeta}>
                <strong>{active.title}</strong>
                <span>
                    {activeDone}/{activeCounted.length} done
                </span>
            </div>
        </div>
    );
};

export default SectionStepper;
